'use client';

import { useEffect, useRef } from 'react';
import { Container, useAutoPlayVideoRef } from '../shared';
import { P } from './StyleConstants';
import type { AnimationItem } from 'lottie-web';
import { useLottieAnimation } from 'hooks/animation/useLottieAnimation';
import { BuySection } from './BuySection';
import { RegionSelector } from 'components/shared/RegionSelector';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './HeroSection.module.css';

export const getSplitChars = (input: string, className: string) => {
  const op = input
    .toUpperCase()
    .split('')
    .map((letter, index) => {
      return (
        <span key={index} className={className}>
          {letter}
        </span>
      );
    });

  return op;
};

export const HeroSection = ({
  className,
  country,
}: {
  className?: string;
  country?: string;
}) => {
  const mobileVideoPath = getCompressedAssetUrl(
      '/web_v2/videos/uhx-gradient.mp4/w_1920',
    );
    const desktopVideoPath = getCompressedAssetUrl(
      '/web_v2/videos/uhx-gradient.mp4/w_1920',
    );

    const videoRef = useAutoPlayVideoRef();

    const animContainerRef = useRef<HTMLDivElement>(null);
    const lottieRef = useRef<AnimationItem | null>(null);

    useLottieAnimation(animContainerRef, {
      path: '/lottie/ultrahumanX-rotate.json',
      renderer: 'svg',
      loop: true,
      autoplay: false,
      onInstance: (animation: AnimationItem) => {
        lottieRef.current = animation;
        if (animation.isPaused) {
          animation.play();
        }
      },
    });

    useEffect(() => {
      const el = animContainerRef.current;
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          const inView = entries[0]?.isIntersecting;
          const anim = lottieRef.current;
          if (!anim) return;
          if (inView) {
            if (anim.isPaused) anim.play();
          } else {
            if (!anim.isPaused) anim.pause();
          }
        },
        { rootMargin: '100px' },
      );
      io.observe(el);
      return () => io.disconnect();
    }, []);

    useEffect(() => {
      const setDesktopVideoIfLargeScreen = () => {
        if (!videoRef.current) {
          return;
        }
        if (
          window.innerWidth > window.innerHeight &&
          (!videoRef.current.src ||
            !videoRef.current.src.endsWith(desktopVideoPath))
        ) {
          videoRef.current.src = desktopVideoPath;
          return;
        }
        if (
          window.innerWidth < window.innerHeight &&
          (!videoRef.current.src ||
            !videoRef.current.src.endsWith(mobileVideoPath))
        ) {
          videoRef.current.src = mobileVideoPath;
          return;
        }
      };

      window.addEventListener('resize', setDesktopVideoIfLargeScreen);
      setDesktopVideoIfLargeScreen();

      return () => {
        window.removeEventListener('resize', setDesktopVideoIfLargeScreen);
      };
    }, []);

    return (
      <section className={`${styles.root}${className ? ` ${className}` : ''}`}>
        <div className={styles['video-container']}>
          <video preload="none" loop autoPlay muted playsInline ref={videoRef}>
            <source src={mobileVideoPath} type="video/mp4" />
          </video>
        </div>
        <div className={styles['hero-container']}>
          <Container className={styles.content}>
            <div
              className="countrySelector"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '0 16px',
              }}
            >
              <RegionSelector />
            </div>
            {/* <Link href="/rare" className="rare-link">
            Checkout exclusive Rare Ring UHX benefits <RightArrow />
          </Link> */}

            <div
              className={styles['lottie-container']}
              ref={animContainerRef}
            ></div>

            <h1>UltrahumanX Membership</h1>
            <P className={styles.description}>
              UltrahumanX is your <b>peace of mind</b>, packed with unbeatable
              benefits. <b>Protection</b> from accidental damage, loss, or
              theft. <b>Priority support</b> and{' '}
              <b>effortless returns and replacements</b>. Everything you need
              and more, seamlessly built in—so you can focus on what matters
              most: <b>your health</b>.
            </P>
          </Container>
        </div>
        <BuySection country={country} />
      </section>
    );
};
